import type {ReactElement} from 'react';
import React from 'react';
import type {DropdownItemStorage} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import {getCurrentPage, getCurrentPageBackground} from '@Components/poster-editor/poster-editor-reducer';
import type {ColorFillStorage} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {ContextualMenuColorFillOption} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {ColorFillPropertyType} from '@Components/color-fill';
import {getColorObjectArrayFromFillColor} from '@Utils/color.util';
import type {ColorBackgroundObject} from '@PosterWhiteboard/page/background/color-background.class';
import {getColorFillTypeForBackgroundDropdownItem, getFillColorForBackgroundDropdownItem} from '@PosterWhiteboard/page/background/color-background.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {ImageBackgroundObject} from '@PosterWhiteboard/page/background/image-background.class';
import {onOpenUserBackgroundUploadsModal, onOpenStockBackgroundModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {ContextualMenuImageThumbnailOption} from '@Components/contextual-menu/components/contextual-menu-image-thumbnail-option';
import {PosterEditingBaseDropdownOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-dropdown-option';
import {PageBackgroundDropDownItem} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-background-option/page-background-option.types';
import {GA4EventName, GA4EventParamName, GA4EventSources, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {useAppSelector} from '@/hooks';
import {getUrlForImageItemBigTierScreenSize, ImageItemSource} from '@/libraries/image-item.library';

interface PageBackgroundOptionProps {
  onClickColorFill(): void;
  onClickEditImageBackground(): void;
}

export function PageBackgroundOption({...props}: PageBackgroundOptionProps): ReactElement<any> | null {
  const currentPageObject = useAppSelector(getCurrentPage);
  const currentBackgroundObject = useAppSelector(getCurrentPageBackground);

  if (!currentBackgroundObject || !currentPageObject) {
    return null;
  }
  const currentPage = window.posterEditor?.whiteboard?.pages.getPage(currentPageObject.hashedID);
  if (!currentPage) {
    return null;
  }

  const getBackgroundDropdownItemForBackgroundObject = (): PageBackgroundDropDownItem => {
    switch (currentBackgroundObject.details.type) {
      case BackgroundTypeName.COLOR:
        return getBackgroundDropdownItemForColorBackgroundObject(currentBackgroundObject.details);
      case BackgroundTypeName.IMAGE:
        return getBackgroundDropdownItemForImageBackgroundObject(currentBackgroundObject.details);
      default:
        throw new Error(`Unhandled backgroundDropDownItem`);
    }
  };

  const getBackgroundDropdownItemForImageBackgroundObject = (imageBackgroundObject: ImageBackgroundObject): PageBackgroundDropDownItem => {
    if (imageBackgroundObject.type === BackgroundTypeName.IMAGE) {
      if (imageBackgroundObject.imageBackgroundItemObject.imageSource === ImageItemSource.BACKGROUND_UPLOAD) {
        return PageBackgroundDropDownItem.MY_BACKGROUNDS;
      }
      return PageBackgroundDropDownItem.STOCK_IMAGE;
    }

    throw new Error(`Function getBackgroundDropdownItemForImageBackgroundObject expected image background object type to be of image, instead got ${imageBackgroundObject.type}`);
  };

  const getBackgroundDropdownItemForColorBackgroundObject = (colorBackgroundObject: ColorBackgroundObject): PageBackgroundDropDownItem => {
    switch (colorBackgroundObject.fill.fillType) {
      case FillTypes.SOLID:
        return PageBackgroundDropDownItem.SOLID;
      case FillTypes.LINEAR_GRADIENT:
      case FillTypes.RADIAL_GRADIENT:
        return PageBackgroundDropDownItem.GRADIENT;
      case FillTypes.NONE:
        return PageBackgroundDropDownItem.TRANSPARENT;
      default:
        throw new Error(`currentBackgroundObject fill type: ${colorBackgroundObject.fill.fillType}`);
    }
  };

  const showSubOptionForColor = (): boolean => {
    return currentBackgroundObject.details.type === BackgroundTypeName.COLOR && currentBackgroundObject.details.fill.fillType !== FillTypes.NONE;
  };

  const showSubOptionForImage = (): boolean => {
    return currentBackgroundObject.details.type === BackgroundTypeName.IMAGE;
  };

  const getDataForColor = (): ColorFillStorage[] => {
    if ('fill' in currentBackgroundObject.details) {
      return [
        {
          id: 'color-background',
          color: getColorObjectArrayFromFillColor(currentBackgroundObject.details.fill.fillColor),
          fillType: getColorFillType(),
        },
      ];
    }

    return [];
  };

  const getColorFillType = (): ColorFillPropertyType => {
    if (!('fill' in currentBackgroundObject.details)) {
      throw new Error('Current background is not of type color');
    }

    if (currentBackgroundObject.details.fill.fillType === FillTypes.LINEAR_GRADIENT) {
      return ColorFillPropertyType.LINEAR;
    }

    if (currentBackgroundObject.details.fill.fillType === FillTypes.RADIAL_GRADIENT) {
      return ColorFillPropertyType.RADIAL;
    }

    return ColorFillPropertyType.SOLID;
  };

  const getImgSrcForBackground = (): string => {
    if (currentBackgroundObject.details.type === BackgroundTypeName.IMAGE) {
      if (currentBackgroundObject.details.imageBackgroundItemObject.hashedFilename && currentBackgroundObject.details.imageBackgroundItemObject.imageSource) {
        return getUrlForImageItemBigTierScreenSize(
          currentBackgroundObject.details.imageBackgroundItemObject.hashedFilename,
          currentBackgroundObject.details.imageBackgroundItemObject.imageSource
        );
      }
      if (currentBackgroundObject.details.imageBackgroundItemObject.uploadingImageData?.dataUrl) {
        return currentBackgroundObject.details.imageBackgroundItemObject.uploadingImageData.dataUrl;
      }
      throw new Error('Neither hashedFilename+Extension nor dataUrl is present');
    }
    throw new Error('Unable to get image source for image background since background type is not IMAGE');
  };

  const onEditImageBackground = (): void => {
    props.onClickEditImageBackground();
  };

  const getSubOptionForBackgroundOption = (): ReactElement<any> | null => {
    if (showSubOptionForColor()) {
      return (
        <ContextualMenuColorFillOption
          id="solid-background-color-fill"
          label={window.i18next.t('pmwjs_color')}
          data={getDataForColor()}
          onClick={(): void => {
            props.onClickColorFill();
          }}
        />
      );
    }
    if (showSubOptionForImage()) {
      return (
        <ContextualMenuImageThumbnailOption
          id="image-background-thumbnail-option"
          label={window.i18next.t('pmwjs_photo')}
          imgSrc={getImgSrcForBackground()}
          onEdit={onEditImageBackground}
        />
      );
    }
    return null;
  };

  const onClickHandler = (clickedId: PageBackgroundDropDownItem): void => {
    if (isBackgroundTypeColor(clickedId) || clickedId === PageBackgroundDropDownItem.TRANSPARENT) {
      void currentPage.background.updateFromObject({
        details: {
          type: BackgroundTypeName.COLOR,
          fill: {
            fillType: getColorFillTypeForBackgroundDropdownItem(clickedId),
            fillColor: getFillColorForBackgroundDropdownItem(clickedId),
          },
        },
      });
      return;
    }

    if (clickedId === PageBackgroundDropDownItem.STOCK_IMAGE) {
      onOpenStockBackgroundModal();
      return;
    }
    if (clickedId === PageBackgroundDropDownItem.MY_BACKGROUNDS) {
      onOpenUserBackgroundUploadsModal();
    }
  };

  const isBackgroundTypeColor = (id: PageBackgroundDropDownItem): boolean => {
    switch (id) {
      case PageBackgroundDropDownItem.GRADIENT:
      case PageBackgroundDropDownItem.SOLID:
        return true;
      default:
        return false;
    }
  };

  const getDropdownItems = (): DropdownItemStorage[] => {
    const dropdownItems: DropdownItemStorage[] = [];
    dropdownItems.push({text: window.i18next.t('pmwjs_solid_color'), id: PageBackgroundDropDownItem.SOLID});
    dropdownItems.push({text: window.i18next.t('pmwjs_background_gradient'), id: PageBackgroundDropDownItem.GRADIENT});
    dropdownItems.push({text: window.i18next.t('pmwjs_transparent'), id: PageBackgroundDropDownItem.TRANSPARENT});
    dropdownItems.push({text: window.i18next.t('pmwjs_my_backgrounds'), id: PageBackgroundDropDownItem.MY_BACKGROUNDS});
    dropdownItems.push({text: window.i18next.t('pmwjs_stock_photo'), id: PageBackgroundDropDownItem.STOCK_IMAGE});

    return dropdownItems;
  };

  return (
    <PosterEditingBaseDropdownOption
      id="design-background-dropdown"
      label={window.i18next.t('pmwjs_background')}
      onClick={onClickHandler}
      dropdownItems={getDropdownItems()}
      selectedItemId={getBackgroundDropdownItemForBackgroundObject()}
      onOpen={(): void => {
        trackPosterBuilderGA4Events(GA4EventName.BACKGROUND_MENU_OPEN, {[GA4EventParamName.SOURCE]: GA4EventSources.CONTEXTUAL_MENU});
      }}
    >
      {getSubOptionForBackgroundOption()}
    </PosterEditingBaseDropdownOption>
  );
}
